import {
  classifyStoredTargeting,
  isTargetingParseable,
  normalizeTargeting,
  prepareTargetingForSave,
} from "domains/specialEvents/targeting"
import type { Pixel, PixelEntry } from "domains/managingJson/store/types"
import type {
  ImagesByGeo,
  Notification,
  NotificationSrc,
  NotificationSrcEntry,
  NotificationsPerGame,
  SpecialEvent,
  SpecialEventGameScreenBanner,
} from "domains/specialEvents/pages/main"
import { v4 as uuidv4 } from "uuid"

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === "object" && value !== null && !Array.isArray(value)
)

export const normalizePixels = (pixels?: PixelEntry[] | null): Pixel[] => (
  (pixels || []).flatMap(pixel => {
    if (typeof pixel === "string") return [{ src: pixel }]
    if (!isRecord(pixel)) return []

    return [{ ...pixel, src: typeof pixel.src === "string" ? pixel.src : "" } as Pixel]
  })
)

export const normalizeImageSources = (
  imageSources?: NotificationSrcEntry[] | null,
): NotificationSrc[] => (
  (imageSources || []).flatMap(imageSource => {
    if (typeof imageSource === "string") return [{ src: imageSource }]
    if (!isRecord(imageSource)) return []

    return [{ ...imageSource, src: typeof imageSource.src === "string" ? imageSource.src : "" }]
  })
)

export const appendUniqueImageSource = (
  imageSources: NotificationSrcEntry[] | null | undefined,
  src: string,
): NotificationSrc[] => {
  const normalized = normalizeImageSources(imageSources)

  if (!src || normalized.some(imageSource => imageSource.src === src)) return normalized

  return [...normalized, { src }]
}

const mergeUniqueImageSources = (
  current: NotificationSrcEntry[] | null | undefined,
  incoming: NotificationSrcEntry[] | null | undefined,
): NotificationSrc[] => {
  const seenSources = new Set<string>()

  return [...normalizeImageSources(current), ...normalizeImageSources(incoming)]
    .filter(imageSource => {
      const src = imageSource.src || ""
      if (seenSources.has(src)) return false

      seenSources.add(src)

      return true
    })
}

export const normalizeSourceImagesByGeo = (
  sourceImagesByGeo?: (ImagesByGeo | null | undefined)[] | null,
): ImagesByGeo[] => {
  const normalizedSources: ImagesByGeo[] = []
  const sources = sourceImagesByGeo || []
  let repairedGlobalIndex: number | undefined

  sources.forEach(source => {
    if (!isRecord(source)) return

    const hasCountry = typeof source.country === "string" && Boolean(source.country.trim())
    const country = hasCountry ? source.country as string : "global"
    const normalizedSource = {
      ...source,
      country,
      imageSources: normalizeImageSources(source.imageSources as NotificationSrcEntry[] | null | undefined),
    }

    if (hasCountry) {
      if (country !== "global" || repairedGlobalIndex === undefined) {
        normalizedSources.push(normalizedSource)

        return
      }

      const repairedSource = normalizedSources[repairedGlobalIndex]
      normalizedSources[repairedGlobalIndex] = {
        ...repairedSource,
        ...normalizedSource,
        country,
        imageSources: mergeUniqueImageSources(repairedSource.imageSources, normalizedSource.imageSources),
      }
      repairedGlobalIndex = undefined

      return
    }

    const globalIndex = normalizedSources.findIndex(existing => existing.country === "global")
    if (globalIndex < 0) {
      normalizedSources.push(normalizedSource)
      repairedGlobalIndex = normalizedSources.length - 1

      return
    }

    const globalSource = normalizedSources[globalIndex]
    normalizedSources[globalIndex] = {
      ...normalizedSource,
      ...globalSource,
      country,
      imageSources: mergeUniqueImageSources(globalSource.imageSources, normalizedSource.imageSources),
    }
  })

  return normalizedSources
}

export const getGlobalImageSources = (notification?: Notification): NotificationSrc[] => {
  const globalSource = notification?.sourceImagesByGeo?.find(
    source => isRecord(source) && source.country === "global",
  )

  return isRecord(globalSource)
    ? normalizeImageSources(globalSource.imageSources as NotificationSrcEntry[] | null | undefined)
    : []
}

const normalizeBanner = (
  banner?: SpecialEventGameScreenBanner,
): SpecialEventGameScreenBanner | undefined => banner
  ? {
    ...banner,
    trackingPixels: normalizePixels(banner.trackingPixels),
  }
  : undefined

// The app matches gameId with strict string equality; a numeric id silently drops the whole game.
const normalizeGameId = (gameId: NotificationsPerGame["gameId"]): NotificationsPerGame["gameId"] => (
  typeof gameId === "number" ? String(gameId) : gameId
)

const normalizeNotificationUids = (notifications: Notification[]): Notification[] => {
  const usedUids = new Set<string>()

  return notifications.map(notification => {
    const existingUid = typeof notification.uid === "string" ? notification.uid.trim() : ""
    const uid = existingUid && !usedUids.has(existingUid) ? existingUid : uuidv4()
    usedUids.add(uid)

    return { ...notification, uid }
  })
}

export const normalizeNotificationsPerGame = (
  notificationsPerGame?: NotificationsPerGame[],
): NotificationsPerGame[] => (notificationsPerGame || []).map(gameConfig => ({
  ...gameConfig,
  gameId: normalizeGameId(gameConfig.gameId),
  notifications: normalizeNotificationUids(gameConfig.notifications || []).map(notification => ({
    ...notification,
    sourceImagesByGeo: normalizeSourceImagesByGeo(notification.sourceImagesByGeo),
    trackingPixels: normalizePixels(notification.trackingPixels),
  })),
  launchGameBanner: normalizeBanner(gameConfig.launchGameBanner),
  endgameBanner: normalizeBanner(gameConfig.endgameBanner),
  // The editor holds raw text while it does not parse; only the save path narrows it back to an object.
  ...(gameConfig.targeting === undefined
    ? {}
    : { targeting: normalizeTargeting(gameConfig.targeting) as NotificationsPerGame["targeting"] }),
}))

export const normalizeSpecialEvent = (event: SpecialEvent): SpecialEvent => ({
  ...event,
  notificationsPerGame: normalizeNotificationsPerGame(event.notificationsPerGame),
})

const sanitizePixels = (pixels?: PixelEntry[] | null): Pixel[] => (
  normalizePixels(pixels).filter(pixel => Boolean(pixel.src))
)

const sanitizeSourceImagesByGeo = (
  sourceImagesByGeo?: (ImagesByGeo | null | undefined)[] | null,
): ImagesByGeo[] => normalizeSourceImagesByGeo(sourceImagesByGeo).map(source => ({
  ...source,
  imageSources: normalizeImageSources(source.imageSources).filter(imageSource => Boolean(imageSource.src)),
}))

const sanitizeBanner = (
  banner?: SpecialEventGameScreenBanner,
): SpecialEventGameScreenBanner | undefined => banner
  ? {
    ...banner,
    image: banner.image || "",
    cta: banner.cta || "",
    trackingPixels: sanitizePixels(banner.trackingPixels),
  }
  : undefined

// `stored` is required, not optional: without it an invalid stored targeting is dropped rather than
// carried, which silently widens the campaign. Callers with nothing stored pass undefined explicitly.
export const prepareNotificationsPerGameForSave = (
  notificationsPerGame: NotificationsPerGame[] | undefined,
  storedNotificationsPerGame: NotificationsPerGame[] | undefined,
): NotificationsPerGame[] => (notificationsPerGame || []).map(gameConfig => {
  // Destructured out so a dropped targeting leaves no key at all, rather than the raw text.
  const { targeting: editedTargeting, ...rest } = gameConfig
  const stored = storedNotificationsPerGame
    ?.find(config => String(config?.gameId) === String(gameConfig.gameId))?.targeting
  // Unparseable text must not widen a restricted campaign, so the stored targeting stays in force.
  // Only a stored value the client reads as rules is re-normalised; anything else is kept verbatim.
  const kind = classifyStoredTargeting(stored)
  const fallback = kind === "none" ? undefined : kind === "rules" ? (prepareTargetingForSave(stored) ?? stored) : stored
  const targeting = isTargetingParseable(editedTargeting) ? prepareTargetingForSave(editedTargeting) : fallback

  return {
    ...rest,
    gameId: normalizeGameId(gameConfig.gameId),
    notifications: normalizeNotificationUids(gameConfig.notifications || []).map(notification => ({
      ...notification,
      sourceImagesByGeo: sanitizeSourceImagesByGeo(notification.sourceImagesByGeo),
      trackingPixels: sanitizePixels(notification.trackingPixels),
    })),
    launchGameBanner: sanitizeBanner(gameConfig.launchGameBanner),
    endgameBanner: sanitizeBanner(gameConfig.endgameBanner),
    ...(targeting === undefined ? {} : { targeting }),
  }
})
